/**
 * Pure, framework-agnostic paste-normalization helpers. Extracted from
 * `useClipboardHandlers` so the hook stays event-handler orchestration and this
 * module stays the clipboard-HTML → Plate-fragment policy (sanitize → parse →
 * strip data-slate → flatten/enrich lists → drop dead images → collapse
 * whitespace → deserialize → layout-tables → strip newlines).
 *
 * Nothing here touches React or the editor's UI; `deserializePastedHtml` takes
 * the editor only to run Plate's `deserializeHtmlElement`. The live image
 * re-host + caret scroll stay in the hook (they are side effects on the mounted
 * editor, not normalization).
 */
import { collapseWhiteSpace, deserializeHtmlElement, isHtmlBlockElement } from 'platejs';
import { sanitizeOfficeHtml } from './sanitizeOfficeHtml';
import { reconstructWordMsoLists } from './wordMsoLists';
import { convertLayoutTablesToColumns } from './layoutTables';

// Normalize the two list-nesting conventions external sources emit so each
// <li> ends up carrying its text DIRECTLY, with depth expressed structurally
// by ancestor <ul>/<ol> nesting. Mirrors @platejs/list's own `transformData`
// list-prep (which the bare `deserializeHtmlElement` path does NOT invoke —
// that hook only fires through `editor.tf.insertData`), in the same order:
//
//   (1) Sibling-nested vs child-nested lists. Some sources (browser default,
//       MS Word) nest a deeper list INSIDE an <li> (`<li>…<ul>…</ul></li>`);
//       Google Docs makes it a SIBLING of the <li> (`<li>…</li><ul>…</ul>`).
//       Pull any <ol>/<ul> that is a CHILD of an <li> out to sit right after
//       that <li>, so both conventions collapse to the sibling form.
//
//   (2) Presentational block wrapper. Google Docs wraps each item's text in an
//       inner `<p role="presentation">`. Left in place, the list deserializer
//       emits a list-style <p> whose only child is that inner <p> — and the
//       editor's "lift nested block children" list normalizer then hoists the
//       text out as a sibling one indent deeper, leaving an EMPTY list marker
//       behind (the empty-bullet + off-by-one-indent paste bug). Unwrap every
//       block-element child of an <li> by pulling its child nodes up so the
//       item holds inline content directly.
//
// Order matters: lift nested lists FIRST (step 1) so an <li>'s only remaining
// block children in step 2 are presentational wrappers, never real nested lists.
export function flattenPastedListItems(root: HTMLElement): void {
    // (1) Lift child-nested <ol>/<ul> to siblings of their <li>.
    const allListItems = Array.from(root.querySelectorAll('li')) as HTMLElement[];
    for (const li of allListItems) {
        const nestedLists = Array.from(li.children).filter((c) => c.tagName === 'UL' || c.tagName === 'OL');
        for (const nested of nestedLists) {
            li.parentNode?.insertBefore(nested, li.nextSibling);
        }
    }
    // (2) Unwrap presentational block wrappers inside each <li>. Re-query
    // because step 1 may have changed the tree.
    for (const li of Array.from(root.querySelectorAll('li')) as HTMLElement[]) {
        const flattened: Node[] = [];
        li.childNodes.forEach((child) => {
            if (child.nodeType === Node.ELEMENT_NODE && isHtmlBlockElement(child as HTMLElement)) {
                child.childNodes.forEach((grandchild) => flattened.push(grandchild));
                return;
            }
            flattened.push(child);
        });
        li.replaceChildren(...flattened);
    }
}

// Recursively walk the DOM tree and annotate <li> elements with `data-indent` +
// `data-list-style-type` so Plate's ListPlugin HTML deserializer can reconstruct
// list marks. Skips elements that already carry `data-indent` (Plate-origin HTML).
//
// Depth precedence: `data-aria-level` (Word-for-web's authoritative depth) →
// `aria-level` (Google Docs) → the structural `depth` counter. listStyleType
// precedence: the <li>'s OWN inline style → the enclosing <ul>/<ol>'s style →
// the tag default. Traverses BOTH child-nested and sibling-nested lists.
export function enrichPastedListElements(el: HTMLElement, depth: number): void {
    const children = Array.from(el.children) as HTMLElement[];
    for (const child of children) {
        const tag = child.tagName;
        if (tag === 'UL' || tag === 'OL') {
            enrichListAtDepth(child, depth);
        } else {
            enrichPastedListElements(child, depth);
        }
    }
}

// Annotate the <li>s of a single <ul>/<ol> at `depth`, then descend into both
// child-nested lists (inside an <li>, depth + 1) and sibling-nested lists (a
// list that is a direct child of this list, depth + 1).
function enrichListAtDepth(list: HTMLElement, depth: number): void {
    const listStyle = list.style.listStyleType || (list.tagName === 'OL' ? 'decimal' : 'disc');
    for (const node of Array.from(list.children) as HTMLElement[]) {
        if (node.tagName === 'LI') {
            if (!node.dataset.indent) {
                // Word-for-web encodes the real level ONLY in data-aria-level (the
                // list is otherwise flat); Google Docs uses aria-level; fall back
                // to the structural depth from ancestor list nesting.
                const dataAriaLevel = node.dataset.ariaLevel;
                const ariaLevel = node.getAttribute('aria-level');
                node.dataset.indent = dataAriaLevel || ariaLevel || String(depth);
            }
            if (!node.dataset.listStyleType) {
                node.dataset.listStyleType = node.style.listStyleType || listStyle;
            }
            for (const grandchild of Array.from(node.children) as HTMLElement[]) {
                if (grandchild.tagName === 'UL' || grandchild.tagName === 'OL') enrichListAtDepth(grandchild, depth + 1);
            }
        } else if (node.tagName === 'UL' || node.tagName === 'OL') {
            enrichListAtDepth(node, depth + 1);
        }
    }
}

// Plate's HTML deserializer already reads paragraph/cell alignment from
// `style="text-align:..."`, but NOT from the legacy `align="..."` attribute that
// Excel and older HTML sources emit on block elements. Normalize the attribute
// into an inline text-align style on the blocks Plate aligns, so the existing
// deserializer lands it on the node's `align` (the same property the toolbar
// AlignMenu sets). Only stamps when the element has no text-align style already.
const ALIGN_BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'LI', 'TD', 'TH']);
export function enrichPastedAlign(root: HTMLElement): void {
    for (const el of Array.from(root.querySelectorAll('[align]')) as HTMLElement[]) {
        if (!ALIGN_BLOCK_TAGS.has(el.tagName)) continue;
        const value = (el.getAttribute('align') || '').toLowerCase();
        if (value !== 'right' && value !== 'center' && value !== 'justify') continue;
        if (!/text-align/i.test(el.getAttribute('style') || '')) el.style.textAlign = value;
    }
}

// The HTML branch of `handlePaste`, kept side-effect free so the paste pipeline
// can be exercised end-to-end in tests against the real editor plugin config.
// Returns the fragment ready to `insertFragment`, or `null` when the HTML
// deserializes to nothing (caller falls back to plain text).
export function deserializePastedHtml(editor: any, html: string): any[] | null {
    // Desktop Word expresses lists as <p style="mso-list:…"> + an <![if !supportLists]>
    // ordinal — BOTH of which sanitizeOfficeHtml strips (:28,:34,:39). Rebuild real
    // <ol>/<ul><li aria-level> first so the existing Word-for-web/Docs list path
    // (enrichPastedListElements) can see them.
    const cleaned = sanitizeOfficeHtml(reconstructWordMsoLists(html));
    const parsed = new DOMParser().parseFromString(cleaned, 'text/html');
    const body = parsed?.body;
    if (!body) return null;
    // Strip `data-slate-*` attributes from anything pasted from another
    // slate-based editor. Plate's deserializer treats `data-slate-node="element"`
    // as "already a slate node, skip plugin parse rules" — which drops every <p>
    // and <h2> from slate-react, leaving only the inline leaves. Removing the
    // attribute lets the normal validNodeName rules fire so block structure
    // survives the paste.
    body.querySelectorAll('[data-slate-node], [data-slate-leaf], [data-slate-string], [data-slate-fragment]').forEach((el) => {
        el.removeAttribute('data-slate-node');
        el.removeAttribute('data-slate-leaf');
        el.removeAttribute('data-slate-string');
        el.removeAttribute('data-slate-fragment');
        el.removeAttribute('data-slate-type');
        el.removeAttribute('data-slate-inline');
        el.removeAttribute('data-slate-void');
        el.removeAttribute('data-slate-marks');
    });
    // Collapse external list-nesting conventions to a single shape BEFORE
    // enrichment so depth/style land on the right <li>s.
    flattenPastedListItems(body);
    enrichPastedListElements(body, 1);
    // Normalize legacy align="..." attrs → text-align style so alignment survives.
    enrichPastedAlign(body);
    // Drop dead Word VML image fallbacks (`file:///` paths the browser can't read).
    body.querySelectorAll('img[src^="file:"]').forEach((el) => el.remove());
    // Run Plate's native whitespace collapsing before the bare element mapper —
    // the bare `deserializeHtmlElement` skips the `collapseWhiteSpace` step the
    // full dispatcher runs, the root cause of inter-block whitespace garbage.
    let normalizedBody: HTMLElement = body;
    try {
        normalizedBody = collapseWhiteSpace(body);
    } catch {
        normalizedBody = body;
    }
    const deserialized: any = (deserializeHtmlElement as any)(editor, normalizedBody);
    // Word/Docs use single-row tables purely for column layout; convert those to
    // Plate column groups. Data tables stay tables.
    const fragment: any = convertLayoutTablesToColumns(Array.isArray(deserialized) ? deserialized : []);
    if (Array.isArray(fragment) && fragment.length) {
        return stripNewlinesFromFragment(fragment);
    }
    return null;
}

const STRUCTURAL_SELECTOR = 'table,ul,ol,li,h1,h2,h3,h4,h5,h6,blockquote,pre,hr';

// True when the clipboard HTML carries content of its OWN — not merely a
// wrapper around the bitmap that Word/Excel/browsers put on the clipboard
// alongside it. Strip every <img> (plus <style>/<script>, whose text content
// would otherwise read as "visible text") and ask whether anything is left.
export function htmlCarriesContentBeyondImages(html: string): boolean {
    if (!html) return false;
    if (typeof DOMParser === 'undefined') return false;
    try {
        const body = new DOMParser().parseFromString(html, 'text/html')?.body;
        if (!body) return false;
        const clone = body.cloneNode(true) as HTMLElement;
        clone.querySelectorAll('img,style,script').forEach((el) => el.remove());
        // Strip whitespace AND zero-width chars (U+200B / U+FEFF) that Word/Docs
        // sprinkle in beside a fallback image — the deserializer drops them too, so
        // they must not count as real content and route us to an empty HTML paste.
        const text = (clone.textContent ?? '').replace(/[\s​﻿]/g, '');
        if (text.length > 0) return true;
        return clone.querySelector(STRUCTURAL_SELECTOR) != null;
    } catch {
        return false;
    }
}

// Word's inline images arrive as `file:///` <img> fallbacks that
// deserializePastedHtml removes (:159). When the HTML wins the bitmap race,
// such an image is dropped with no replacement — the one case where routing
// silently loses content.
export function hasWordImageFallback(html: string): boolean {
    return /<img[^>]+src\s*=\s*["']?file:/i.test(html);
}

// Zero-width space (U+200B) + BOM / zero-width no-break space (U+FEFF). Word and
// Docs sprinkle these around bullets and inline-formatting boundaries; they
// render as invisible garbage inside a Plate leaf.
const ZERO_WIDTH_RE = /[﻿​]/g;

function cleanLeafText(t: string): string {
    let cleaned = t.replace(ZERO_WIDTH_RE, '').replace(/ /g, ' ');
    if (!/[\r\n]/.test(cleaned)) return cleaned;
    // Newline runs → single space (so inline `foo\nbar` keeps a word boundary),
    // then trim the edge whitespace those newlines introduced.
    cleaned = cleaned.replace(/[\r\n]+/g, ' ').trim();
    return cleaned;
}

// Table structural nodes are load-bearing even when empty — a Word table where
// only the first cell has content must keep its trailing empty cells/rows, not
// have them popped as "trailing blank blocks".
const TABLE_STRUCTURE_TYPES = new Set(['table', 'tr', 'td', 'th']);

function isBlankNode(node: any): boolean {
    if (!node) return false;
    if (typeof node.text === 'string') return cleanLeafText(node.text) === '';
    if (node.type === 'image') return false;
    if (node.type && TABLE_STRUCTURE_TYPES.has(node.type)) return false;
    if (Array.isArray(node.children)) return node.children.every(isBlankNode);
    return false;
}

// Split a block whose direct text leaves span MULTIPLE visible lines into one
// block per line, cloning the block's props (listStyleType, indent, align …)
// onto each line and dropping the source id (two blocks must not share it).
// Returns null when the block doesn't qualify — fewer than two visible lines,
// or no newline-bearing direct text leaf — so the caller falls back to the
// plain per-leaf strip. Non-text inlines (links, mentions) stay in the line
// segment where they sit.
function splitBlockAtNewlines(block: any): any[] | null {
    const kids: any[] = Array.isArray(block?.children) ? block.children : [];
    if (!kids.some((k) => typeof k?.text === 'string' && /[\r\n]/.test(k.text))) return null;
    const segments: any[][] = [[]];
    for (const kid of kids) {
        if (typeof kid?.text === 'string' && /[\r\n]/.test(kid.text)) {
            const parts = kid.text.split(/(?:\r?\n)+/);
            parts.forEach((part, i) => {
                if (i > 0) segments.push([]);
                const text = cleanLeafText(part);
                if (text) segments[segments.length - 1].push({ ...kid, text });
            });
        } else {
            segments[segments.length - 1].push(kid);
        }
    }
    const hasContent = (seg: any[]) => seg.some((k) => (typeof k?.text === 'string' ? k.text.trim() !== '' : true));
    const lines = segments.filter(hasContent);
    if (lines.length < 2) return null;
    const { children: _children, id: _id, ...props } = block;
    // A `listRestart` pins the START of this block's Word counter — a split
    // continuation line (from a <w:br> inside a numbered paragraph) is never a
    // new counter start, so only the FIRST line keeps it. Without this every
    // split line would carry the same listRestart and render e.g. "3. 3. 3."
    // instead of "3. 4. 5.".
    const { listRestart: _listRestart, ...continuationProps } = props;
    return lines.map((seg, i) => ({ ...(i === 0 ? props : continuationProps), children: seg }));
}

// Normalize raw newline characters in a pasted fragment's text leaves. The
// editor renders `white-space: pre-wrap`, so a "\n" inside a leaf IS a visible
// line break — the report generator emits bullet lists as ONE leaf with
// "•"-prefixed lines separated by \n. Two cases:
//   - a leaf spanning MULTIPLE visible lines → split its block into one block
//     per line (joining them with spaces collapsed pasted bullet lists into a
//     single run-on paragraph — RECUR-26249);
//   - anything else (stray trailing newline, newline-only spacer leaf) → the
//     old collapse-to-space/empty cleanup.
// Done at PASTE time only (not as a live normalizer) so Shift+Enter
// soft-breaks already in the document are never disturbed.
export function stripNewlinesFromFragment(nodes: any[]): any[] {
    if (!Array.isArray(nodes)) return nodes;
    const cleaned = nodes.flatMap((node) => {
        if (node && typeof node.text === 'string') {
            const text = cleanLeafText(node.text);
            return [text === node.text ? node : { ...node, text }];
        }
        if (node && Array.isArray(node.children)) {
            const split = splitBlockAtNewlines(node);
            if (split) return split.map((line) => ({ ...line, children: stripNewlinesFromFragment(line.children) }));
            return [{ ...node, children: stripNewlinesFromFragment(node.children) }];
        }
        return [node];
    });
    // Remove a single trailing blank block at the fragment tail. Interior blank
    // paragraphs are intentional spacing and must survive.
    if (cleaned.length > 1 && isBlankNode(cleaned[cleaned.length - 1])) {
        cleaned.pop();
    }
    return cleaned;
}

// `application/x-slate-fragment` codec. Slate's canonical encoding is base64
// over the URI-encoded JSON (slate-react setFragmentData:
// `btoa(encodeURIComponent(JSON.stringify(fragment)))`) — WRITE that format so
// any slate-based reader can decode our clipboard. READ both that and the raw
// URI-encoded form this editor wrote before 2026-07 (old copies can outlive a
// deploy on the OS clipboard). The two are unambiguous: base64 of a fragment
// never starts with '%', and raw '%5B…' contains '%' which atob rejects.
export function encodeSlateFragment(fragment: any[]): string {
    return window.btoa(encodeURIComponent(JSON.stringify(fragment)));
}

export function decodeSlateFragment(raw: string): any[] | null {
    for (const decode of [() => JSON.parse(decodeURIComponent(window.atob(raw))), () => JSON.parse(decodeURIComponent(raw))]) {
        try {
            const value = decode();
            if (Array.isArray(value) && value.length) return value;
        } catch {
            /* not this encoding — try the next */
        }
    }
    return null;
}

// The base `editor.api.fragment()` slices the document with the ancestor chain
// intact, so ANY selection inside one table cell — a word, a line, the whole
// cell — comes back wrapped as a 1×1 table (table > tr > td > content). Left on
// the clipboard, that wrapper makes the paste side treat a copied WORD as a
// TABLE paste (spreadTableIntoDestination) and replace the destination cell's
// children instead of inserting at the caret. @platejs/table's own getFragment
// override unwraps exactly this shape, but the custom copy path bypasses it —
// mirror the unwrap here: a table node with one row and one cell is replaced by
// that cell's content, in place. Genuine (multi-cell) tables pass through
// untouched, so rectangle copies keep their table shape.
export function unwrapSingleCellTables(fragment: any[]): any[] {
    if (!Array.isArray(fragment)) return fragment;
    const out: any[] = [];
    for (const node of fragment) {
        if (node?.type === 'table') {
            const rows: any[] = Array.isArray(node.children) ? node.children : [];
            const cells: any[] = Array.isArray(rows[0]?.children) ? rows[0].children : [];
            if (rows.length === 1 && cells.length === 1) {
                out.push(...(Array.isArray(cells[0]?.children) ? cells[0].children : []));
                continue;
            }
        }
        out.push(node);
    }
    return out;
}

// Visual column count of a table node. Cells with colSpan (or the legacy
// attributes.colspan) occupy that many columns, so children.length undercounts
// any merged row — and colSizes arrays are per VISUAL column.
export function tableFragmentColumnCount(table: any): number {
    const rows: any[] = Array.isArray(table?.children) ? table.children : [];
    return Math.max(
        0,
        ...rows.map((row: any) =>
            (Array.isArray(row?.children) ? row.children : []).reduce(
                (n: number, cell: any) => n + (Number(cell?.colSpan ?? cell?.attributes?.colspan) || 1),
                0,
            ),
        ),
    );
}

// Re-attach table `colSizes` to a copied fragment. `editor.api.fragment()` can
// return a table WITHOUT its table-level `colSizes`, so a Plate-to-Plate paste
// loses the column widths. Read the source tables from the current selection
// and copy their colSizes onto the fragment's tables by structural match
// (source colSizes.length === fragment table's column count), consuming each
// source entry at most once. Matching by column count rather than document-order
// index avoids mispairing when the fragment contains more or fewer tables than
// the source selection (e.g. a partial paste from a multi-table selection).
export function reattachTableColSizes(editor: any, fragment: any[]): void {
    try {
        // Collect available source colSizes from the current editor selection.
        const available: Array<{ colSizes: number[]; used: boolean }> = [];
        const entries = editor?.api?.nodes?.({ match: (n: any) => n?.type === 'table' });
        if (entries) {
            for (const [node] of entries) {
                if (Array.isArray(node?.colSizes) && (node.colSizes as number[]).length > 0) {
                    available.push({ colSizes: node.colSizes as number[], used: false });
                }
            }
        }
        if (!available.length) return;

        const walk = (nodes: any[]): void => {
            for (const node of nodes) {
                if (node?.type === 'table') {
                    // Only attach when the fragment table has no colSizes yet.
                    if (!Array.isArray(node.colSizes) || node.colSizes.length === 0) {
                        const colCount = tableFragmentColumnCount(node);
                        // Find the first unused source whose colSizes length matches the
                        // fragment table's column count. This is a structural match, not
                        // a positional one, so a paste that reorders or subsets tables
                        // still lands the right widths on the right table.
                        const match = available.find((s) => !s.used && s.colSizes.length === colCount);
                        if (match) {
                            node.colSizes = [...match.colSizes];
                            match.used = true;
                        }
                    }
                }
                if (Array.isArray(node?.children)) walk(node.children);
            }
        };
        walk(fragment);
    } catch {
        /* defensive — copy still works without colSizes */
    }
}
